import { computed } from 'vue'

/**
 * Composable to check for children without tickets and provide IDs for bulk purchase
 */
export function useChildrenTickets() {
  const membersStore = useMembersStore()
  const currentUser = useCurrentUser()
  const { paradeYear } = useParadeYear()

  // Get current member
  const currentMember = computed(() => {
    if (!currentUser.value?.uid) return null

    const memberPath = `members/${currentUser.value.uid}`
    return membersStore.members[memberPath] || null
  })

  // A child has a ticket when a completed children purchase for the parade
  // year lists them, as check-in counts it (useCheckinStatus)
  const hasChildTicket = (childId: string) => {
    const member = currentMember.value
    if (!member?.children || !member?.purchases) return false

    const yearPurchases = member.purchases[paradeYear.value]
    if (yearPurchases?.children) {
      // Look through all children ticket purchases for this year
      const childrenPurchases = yearPurchases.children
      if (Array.isArray(childrenPurchases)) {
        // Check if any purchase includes this child's ID in childrenIds
        return childrenPurchases.some(purchase =>
          purchase.status === 'completed'
          && Array.isArray(purchase.childrenIds)
          && purchase.childrenIds.includes(childId),
        )
      }
    }

    return false
  }

  // Check if member is in a sponsored subkrewe (children exempt from tickets)
  const isSponsored = computed(() => {
    const member = currentMember.value
    return member?.subkrewe?.sponsored === true
  })

  // Get children without tickets
  const childrenWithoutTickets = computed(() => {
    const member = currentMember.value
    if (!member?.children) return []

    // Sponsored subkrewe members don't need to pay for children's tickets
    if (isSponsored.value) return []

    // Get all child IDs that don't have tickets
    return Object.keys(member.children).filter(childId => !hasChildTicket(childId))
  })

  // Check if there are any children without tickets
  const hasChildrenWithoutTickets = computed(() => {
    return childrenWithoutTickets.value.length > 0
  })

  return {
    childrenWithoutTickets,
    hasChildrenWithoutTickets,
  }
}
